import { useCallback, useEffect, useMemo, useState } from 'react'
import { SingleSheetPropertyValueType } from '../model/singleSheetProperty'
import { intl } from '../../../../i18n'
import { WbsItemAdditionalPropertyEntity } from '../../../../domain/entity/WbsItemAdditionalPropertyEntity'
import { BasicInformatioTableDef, CellDef } from '../table/model'
import usePrevious from '../../../hooks/usePrevious'
import { isDateTermV2 } from '../../../../domain/value-object/DateTermV2'
import { isDateTimeRangeV2 } from '../../../../domain/value-object/DateTimeRangeV2'
import { dateV2Service } from '../../../../domain/value-object/DateV2'
import { MultiLineTextTabDef } from '../tab/model'
import { containsDisallowedCharacters } from './validateCharactersAllowed'
import { validateEmail } from './validateEmail'
import { exceedsMaxDigits } from './validateNumberDigits'
import { isRequiredValueMissing } from './validateRequiredValue'
import { getSlackUserSelectionState } from './slackUserSelection'

export const useSingleSheetFormValidation = (
  basicInformatioTableDef: BasicInformatioTableDef,
  multiLineTextTabDefs: MultiLineTextTabDef[],
  edited: boolean
): {
  validate: ValidateSingleSheetPropertyValue
  validateAllProperties: ValidateAllSingleSheetPropertyValues
  validationErrors: SingleSheetPropertiesValidationErrors
  hasValidationErrors: boolean
} => {
  const cellDefMap = useMemo(() => {
    const map: CellDefMap = {}
    basicInformatioTableDef.tables.forEach(v => {
      v.rows.forEach(row => {
        row.forEach(cell => {
          map[cell.field] = cell
        })
      })
    })
    return map
  }, [basicInformatioTableDef])
  const tabDefMap = useMemo(() => {
    const map: TabDefMap = {}
    multiLineTextTabDefs.forEach(v => {
      map[v.field] = v
    })
    return map
  }, [multiLineTextTabDefs])
  const [validationErrors, setValidationErrors] =
    useState<SingleSheetPropertiesValidationErrors>(
      {} as SingleSheetPropertiesValidationErrors
    )
  const [hasValidationErrors, setHasValidationErrors] = useState<boolean>(false)

  const validate = useCallback(
    (field: string, value: SingleSheetPropertyValueType) => {
      const cellDef = cellDefMap[field]
      const validationErrors: SingleSheetPropertyValidationError[] = []
      if (cellDef) {
        validationErrors.push(...validateCellValue(cellDef, value))
        // validateCellValueに含めず個別に呼ぶ理由はvalidateCellValue参照。
        if ('MULTI_LINE_TEXT' === cellDef.type) {
          const multiLineTextError = validateMultiLineText(value)
          multiLineTextError && validationErrors.push(multiLineTextError)
        }
      } else {
        const tabDef = tabDefMap[field]
        if (!tabDef) return
        const multiLineTextError = validateMultiLineText(value)
        multiLineTextError && validationErrors.push(multiLineTextError)
      }

      setValidationErrors(prev => {
        return {
          ...prev,
          [field]: validationErrors,
        }
      })
    },
    [cellDefMap, tabDefMap]
  )

  const validateAllProperties = useCallback(
    (formModel: object): ReturnType<ValidateAllSingleSheetPropertyValues> => {
      const allPropertiesValidationErrors: ReturnType<ValidateAllSingleSheetPropertyValues> =
        {}
      Object.entries(cellDefMap).forEach(([field, cellDef]) => {
        if (!cellDef.editable) return
        const value = cellDef.getValue
          ? cellDef.getValue(formModel)
          : formModel[field]
        const validationErrors = validateCellValue(cellDef, value)

        if (validationErrors.length === 0) return

        allPropertiesValidationErrors[field] = {
          label: cellDef.label,
          validationErrors,
        }
      })
      multiLineTextTabDefs.forEach(multiLineTextTabDef => {
        const value = formModel[multiLineTextTabDef.field]
        if (!value) return

        const validationError = validateMultiLineText(value)
        if (!validationError) return

        allPropertiesValidationErrors[multiLineTextTabDef.field] = {
          label: multiLineTextTabDef.label,
          validationErrors: [validationError],
        }
      })

      setValidationErrors(
        Object.fromEntries(
          Object.entries(allPropertiesValidationErrors).map(
            ([field, validationResult]) => [
              field,
              validationResult.validationErrors,
            ]
          )
        )
      )
      return allPropertiesValidationErrors
    },
    [cellDefMap, multiLineTextTabDefs]
  )

  const prevEdited = usePrevious(edited)
  useEffect(() => {
    if (prevEdited && !edited) {
      setValidationErrors({})
    }
  }, [edited])

  useEffect(() => {
    const hasErrors = Object.values(validationErrors).some(
      v => (v as SingleSheetPropertyValidationErrors).length > 0
    )
    setHasValidationErrors(hasErrors)
  }, [validationErrors])

  return {
    validate,
    validateAllProperties,
    validationErrors,
    hasValidationErrors,
  }
}

// Validators.
// 入力中（validate）と保存時（validateAllProperties）の両方で行う検証。
// MULTI_LINE_TEXTの文字数チェックだけは保存時に行わない既存仕様（tabベースの
// multiLineTextTabDefsは保存時に別途チェックしている）のため、ここに含めると
// validateAllPropertiesへ波及してしまう。validate側で個別に呼び出している。
const validateCellValue = (
  cellDef: CellDef,
  value: SingleSheetPropertyValueType
): SingleSheetPropertyValidationError[] =>
  [
    validateRequiredProperty(cellDef, value),
    validateDateTerm(cellDef, value),
    validateDateTimeRange(cellDef, value),
    'SLACK_USER' === cellDef.type && !cellDef.required
      ? validateSlackUserCompleteness(value)
      : undefined,
    'EMAIL' === cellDef.type
      ? validateEmailProperty(value, cellDef.emailDomainWhitelist)
      : undefined,
    validateMaxLength(cellDef, value),
    validateNumberMaxDigits(cellDef, value),
    validateCharactersAllowedProperty(value, cellDef.charactersAllowed),
  ].filter((v): v is SingleSheetPropertyValidationError => !!v)
const validateRequiredProperty = (
  cellDef: CellDef,
  value: SingleSheetPropertyValueType
): SingleSheetPropertyValidationError | undefined => {
  if (!cellDef.required) return undefined
  if (!isRequiredValueMissing(cellDef.type, value)) return undefined
  return intl.formatMessage(
    { id: 'validator.required' },
    {
      field: cellDef.label,
    }
  )
}
const validateDateTerm = (
  cellDef: CellDef,
  value: SingleSheetPropertyValueType
) => {
  if (cellDef.type !== 'DATE_TERM' || !isDateTermV2(value)) return
  const { startDate, endDate } = value
  if (
    startDate &&
    endDate &&
    !dateV2Service.isEqualOrBefore(startDate, endDate)
  ) {
    return intl.formatMessage({ id: 'validator.dateRange.illegalOrder' })
  }

  return
}
// DATE_TERM と同じ前後関係チェック。日時なので時刻まで含めて比較する。
const validateDateTimeRange = (
  cellDef: CellDef,
  value: SingleSheetPropertyValueType
) => {
  if (cellDef.type !== 'DATE_TIME_RANGE' || !isDateTimeRangeV2(value)) return
  const { startDateTime, endDateTime } = value
  if (
    startDateTime &&
    endDateTime &&
    startDateTime.getTime() > endDateTime.getTime()
  ) {
    return intl.formatMessage({ id: 'validator.dateTimeRange.illegalOrder' })
  }

  return
}
// required:falseのセルでも中間状態（incomplete、slackUserSelection.ts参照）は
// 保存できないため別途チェックする。required:trueのセルはisRequiredValueMissingが
// 同じ状態をvalidator.requiredとして返すので、二重にエラーが出ないよう呼び出し側の
// validateCellValueでrequired:falseのときだけ呼んでいる。
const validateSlackUserCompleteness = (
  value: SingleSheetPropertyValueType
): SingleSheetPropertyValidationError | undefined => {
  if (getSlackUserSelectionState(value) !== 'incomplete') {
    return undefined
  }
  return intl.formatMessage({ id: 'validator.slackUser.incomplete' })
}
// 最大文字数チェック（TEXT / EMAIL など文字列値の cellDef.maxLength を検証）。
const validateMaxLength = (
  cellDef: CellDef,
  value: SingleSheetPropertyValueType
): SingleSheetPropertyValidationError | undefined => {
  if (cellDef.maxLength === undefined) return undefined
  if (typeof value !== 'string') return undefined
  if (value.length <= cellDef.maxLength) return undefined
  return intl.formatMessage(
    { id: 'validator.character.maxLength' },
    { maxLength: cellDef.maxLength }
  )
}
// NUMBERセルの値はnumberのため、文字列前提のvalidateMaxLengthでは検証できない。
// maxLengthを桁数の上限として別途検証する。
const validateNumberMaxDigits = (
  cellDef: CellDef,
  value: SingleSheetPropertyValueType
): SingleSheetPropertyValidationError | undefined => {
  if (cellDef.type !== 'NUMBER') return undefined
  if (!exceedsMaxDigits(value, cellDef.maxLength)) return undefined
  return intl.formatMessage(
    { id: 'validator.number.maxDigits' },
    { maxDigits: cellDef.maxLength }
  )
}
const validateCharactersAllowedProperty = (
  value: SingleSheetPropertyValueType,
  charactersAllowed?: string
): SingleSheetPropertyValidationError | undefined => {
  if (typeof value !== 'string') return undefined
  if (!containsDisallowedCharacters(value, charactersAllowed)) return undefined
  return (
    intl.formatMessage({ id: 'validator.invalidCharacter.allowed' }) +
    charactersAllowed
  )
}
const validateEmailProperty = (
  value: SingleSheetPropertyValueType,
  emailDomainWhitelist?: string[]
): SingleSheetPropertyValidationError | undefined => {
  if (typeof value !== 'string') return undefined
  const errorType = validateEmail(value, emailDomainWhitelist)
  switch (errorType) {
    case 'CONTAINS_FULL_WIDTH':
      return intl.formatMessage({ id: 'validator.email.fullWidth' })
    case 'INVALID_FORMAT':
      return intl.formatMessage({ id: 'validator.email.invalidFormat' })
    case 'INVALID_DOMAIN':
      // 旧仕様に合わせ、許可ドメイン一覧をメッセージ末尾に付与する。
      return (
        intl.formatMessage({ id: 'validator.invalidEmailDomain' }) +
        (emailDomainWhitelist ?? []).join(', ')
      )
    default:
      return undefined
  }
}
const validateMultiLineText = (value: SingleSheetPropertyValueType) => {
  if (value && typeof value === 'string' && value.length > 10000) {
    return intl.formatMessage(
      { id: 'validator.character.maxLength' },
      { maxLength: 10000 }
    )
  }

  return
}

// Public types.
export type SingleSheetPropertyValidationError = string
export type SingleSheetPropertyValidationErrors =
  SingleSheetPropertyValidationError[]
export type SingleSheetPropertiesValidationErrors = {
  [field: string]: SingleSheetPropertyValidationError[]
}
export type ValidateSingleSheetPropertyValue = (
  field: string,
  value: SingleSheetPropertyValueType
) => void
export type ValidateAllSingleSheetPropertyValues = (formModel: object) => {
  [field: string]: {
    label: string
    validationErrors: SingleSheetPropertyValidationError[]
  }
}
export type ValidateSingleSheetWbsItemAdditionalPropertyValue = (
  wbsItemAdditionalProperty: WbsItemAdditionalPropertyEntity,
  value: SingleSheetPropertyValueType
) => void
export type ValidateSingleSheetEntityExtensionPropertyValue = (
  value: SingleSheetPropertyValueType
) => void

// Private types.
type CellDefMap = {
  [key: string]: CellDef
}
type TabDefMap = {
  [key: string]: MultiLineTextTabDef
}
